import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDebouncedCallback } from 'use-debounce'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TableContainer from '@mui/material/TableContainer'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'

import { useCheckIfExists } from '@/hooks/useCheckIfExists'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import { AddDoctorFormValues } from '@/components/group/AddDoctorFormDialog'
import PageContainer from '@/components/ui/containers/PageContainer'
import Pagination from '@/components/ui/pagination/Pagination'
import TextInput from '@/components/ui/inputs/TextInput'
import AddDoctorDialog from '@/containers/group/AddDoctorDialog'

import { Staff } from '@/core/models/staff.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  getStaffs,
  selectStaffSearchResult,
  selectStaffListRequestTracker,
  selectStaffSearchParams,
  selectStaffs,
  handleContact,
} from '@/redux/reducers/staff.reducer'
import { addMedecin, selectMedecinCreateRequestTracker, selectNewlyCreatedMedecinId } from '@/redux/reducers/medecin.reducer'

import { checkIfObjectId } from '@/utils/helpers'
import { sanitizeStaffCreation } from '@/utils/staff'

import StaffsTable, { HandleContactAction, StaffData } from './components/StaffsTable'
import { isStaff } from '@/utils/type-guards'

const rowsPerPageOptions = [5, 10, 15]

export function StaffsPage() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const textInputRef = useRef<HTMLInputElement>(null)

  const addDoctorRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.medecin.create.success'), { variant: 'success' })
      setAddDoctorDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.medecin.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectMedecinCreateRequestTracker,
  })

  const tabs = useMemo(
    () => [
      { label: t('page.staffs.tabs.doctor.label'), value: 'MEDECIN' },
      { label: t('page.staffs.tabs.pds.label'), value: 'PDS' },
    ],
    [t]
  )

  const [itemsPerPage, setItemsPerPage] = useState<number>(rowsPerPageOptions[0])
  const [page, setPage] = useState<number>(1)
  const [selectedTab, setSelectedTab] = useState<string>(tabs[0].value)
  const [addDoctorDialogOpen, setAddDoctorDialogOpen] = useState<boolean>(false)

  const staffs = useAppSelector(selectStaffs)
  const staffSearchParams = useAppSelector(selectStaffSearchParams)
  const staffSearchResult = useAppSelector(selectStaffSearchResult)
  const staffListRequestTracker = useAppSelector(selectStaffListRequestTracker)
  const defaultTextSearch = _.get(location.state, 'from.pattern') === '/staffs/:staffId' ? _.get(staffSearchParams, 'query.name', '') : ''
  const authUser = useAppSelector(selectAuthUser)

  const staffsData = useMemo(() => {
    const getContactStatus = (staffId: string): StaffData['contactStatus'] => {
      const contacts = isStaff(authUser) ? authUser.contacts : []
      const contact = _.find(contacts, ({ contact }) => contact === staffId)

      if (!contact) return 'notRequested'

      switch (contact.status) {
        case 'pending':
          return contact.requestBy === authUser?._id ? 'pending' : 'handle'
        case 'accepted':
        case 'refused':
          return contact.status
        default:
          return 'notRequested'
      }
    }

    return _.map(staffs, (staff) => ({
      id: staff._id,
      name: staff.userInfo.name,
      surname: staff.userInfo.surname,
      gender: staff.userInfo.gender || 'I',
      city: staff.contactInfo?.city,
      cps: staff.cps,
      email: staff.email,
      contactStatus: getContactStatus(staff._id),
      pluginId: staff.pluginId ? staff.pluginId : '',
    }))
  }, [authUser, staffs])

  // launch search staffs when component is mounted
  // keep the previous search params if back from info patient page
  useEffect(() => {
    if (!authUser) return

    const options = {
      limit: rowsPerPageOptions[0],
      skip: 0,
      orderBy: 'contacts',
    }

    const query: Record<string, any> = {
      name: '',
      surname: '',
    }

    if (isStaff(authUser) && authUser.status === 'MEDECIN') {
      query.status = selectedTab
    }

    dispatch(getStaffs(query, options))
  }, [authUser, dispatch, selectedTab])

  const debouncedOnChange = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const textSearch = event.target.value
    const query = _.merge({}, _.get(staffSearchParams, 'query', {}), {
      name: textSearch,
      surname: textSearch,
      cps: textSearch,
    })
    dispatch(
      getStaffs(query, {
        limit: _.get(staffSearchParams.query, 'options.limit', rowsPerPageOptions[0]),
        orderBy: 'contacts',
      })
    )
  }, 300)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    dispatch(
      getStaffs(staffSearchParams.query, {
        limit: itemsPerPage,
        skip: (newPage - 1) * itemsPerPage,
        orderBy: 'contacts',
      })
    )
  }

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    if (itemsPerPage === newItemsPerPage) return
    setPage(1)
    setItemsPerPage(newItemsPerPage)
    dispatch(getStaffs(staffSearchParams.query, { limit: newItemsPerPage, orderBy: 'contacts' }))
  }

  const checkStaff = useCheckIfExists<Staff>({
    successMsg: t('notification.medecin.create.success'),
    errorMsg: t('notification.medecin.create.error'),
    itemTracker: selectMedecinCreateRequestTracker,
    createAction: (staff: Staff) => addMedecin(sanitizeStaffCreation(_.omit(staff, ['_id', 'id', 'contactStatus']))),
    latestIDSelector: useAppSelector(selectNewlyCreatedMedecinId),
    itemList: useAppSelector(selectStaffs),
    callback: (staff: Staff) => {
      if (!staff) return
      // replace: true doesnt seem to work...
      handleContactHandling(staff._id, 'request')
    },
  })

  const handleContactHandling = (contactId: string, action: HandleContactAction) => {
    if (checkIfObjectId(contactId)) {
      if (authUser)
        dispatch(
          handleContact({
            contact: contactId,
            requestBy: authUser._id,
            ...(action === 'accept' && { ok: true }),
            ...(action === 'refuse' && { ok: false }),
          })
        )
    } else {
      checkStaff(contactId)
    }
  }

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
    if (textInputRef.current) textInputRef.current.value = ''
  }

  const handleAddDoctor = (values: AddDoctorFormValues) => {
    addDoctorRequestHandler.dispatch(addMedecin(sanitizeStaffCreation(values)))
  }

  const toggleAddDoctorDialog = () => {
    setAddDoctorDialogOpen(!addDoctorDialogOpen)
  }

  return (
    <PageContainer maxWidth="lg">
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          {isStaff(authUser) && authUser?.status === 'MEDECIN' && (
            <Tabs value={selectedTab} onChange={handleChangeTab}>
              {tabs.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
          )}
          <Button disableElevation onClick={toggleAddDoctorDialog} variant="contained">
            {t('page.staffs.createMedecinButton.label')}
          </Button>
        </Stack>
        <TextInput
          inputRef={textInputRef}
          defaultValue={defaultTextSearch}
          fullWidth
          onChange={debouncedOnChange}
          placeholder={t('page.staffs.searchInput.placeholder')}
        />
        <TableContainer component={Paper}>
          <StaffsTable data={staffsData} loading={staffListRequestTracker.loading} onHandleContact={handleContactHandling} />
          <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
            <Pagination
              color="primary"
              itemsPerPageOptions={rowsPerPageOptions}
              shape="rounded"
              page={page}
              onPageChange={handleChangePage}
              onItemsPerPageChange={handleChangeItemsPerPage}
              totalItems={_.get(staffSearchResult, 'totalCount', 0)}
            />
          </Box>
        </TableContainer>
        <AddDoctorDialog
          isAdding={addDoctorRequestHandler.requestTracker.loading}
          open={addDoctorDialogOpen}
          onAddDoctor={handleAddDoctor}
          onClose={toggleAddDoctorDialog}
        />
      </Stack>
    </PageContainer>
  )
}

export default StaffsPage
